import { ThreeViewport } from './three-viewport'

type MeshVariant = 'orc' | 'hardsurface' | 'stylized'
type MapType = 'pbr' | 'default' | 'albedo' | 'normal' | 'roughness' | 'metallic' | 'wireframe'

interface ViewportData {
  id: string
  viewport: ThreeViewport | null
}

// Prompt and source mesh info per mesh variant
const MESH_INFO: Record<MeshVariant, { prompt: string; downloadUrl: string; imageRef?: string }> = {
  'stylized': {
    prompt: '"stylized knight, hand painted accents, clean transitions"',
    downloadUrl: '/assets/SourceMeshes/stylized-knight.glb',
    imageRef: '/assets/PromptImages/knight-ref.jpg'
  },
  'orc': {
    prompt: '"hyper-realistic aged orc skin texture, scarred, inspired by Weta Workshop"',
    downloadUrl: '/assets/SourceMeshes/photoreal-orc.glb'
  },
  'hardsurface': {
    prompt: '"highly stylized and detailed pistol, combining elements of colt 1911 and revolver, steampunk influence, inspired by Arcane"',
    downloadUrl: '/assets/SourceMeshes/stylized-pistol.glb'
  }
}

// Map mesh variant to image folder name
const MESH_IMAGE_FOLDER: Record<MeshVariant, string> = {
  'stylized': 'Knight',
  'orc': 'Orc',
  'hardsurface': 'Pistol'
}

// Configure model paths for each viewport and mesh variant
// Format: { viewportId: { meshVariant: 'path/to/model.glb' } }
const MODEL_PATHS: Record<string, Record<MeshVariant, string>> = {
  'competitor-a': {
    'stylized': '/assets/CompetitorModels/Knight/competitor-a.glb',
    'orc': '/assets/CompetitorModels/Orc/competitor-a.glb',
    'hardsurface': '/assets/CompetitorModels/Pistol/competitor-a.glb'
  },
  'competitor-b': {
    'stylized': '/assets/CompetitorModels/Knight/competitor-b.glb',
    'orc': '/assets/CompetitorModels/Orc/competitor-b.glb',
    'hardsurface': '/assets/CompetitorModels/Pistol/competitor-b.glb'
  },
  'bakehorse': {
    'stylized': '/assets/CompetitorModels/Knight/bakehorse.glb',
    'orc': '/assets/CompetitorModels/Orc/bakehorse.glb',
    'hardsurface': '/assets/CompetitorModels/Pistol/bakehorse.glb'
  }
}

// Annotation position + pixel offsets per mesh+competitor
interface AnnotationInfo { pos: string; offsetX: number; offsetY: number }
const ANNOTATION_POS: Record<MeshVariant, Record<string, AnnotationInfo>> = {
  'stylized': {
    'competitor-a': { pos: 'bottom-left', offsetX: -170, offsetY: 0 },
    'competitor-b': { pos: 'top-right', offsetX: 150, offsetY: -10 }
  },
  'orc': {
    'competitor-a': { pos: 'left-center', offsetX: -205, offsetY: -200 },
    'competitor-b': { pos: 'top-right', offsetX: 180, offsetY: -50 }
  },
  'hardsurface': {
    'competitor-a': { pos: 'bottom-left', offsetX: -75, offsetY: -110 },
    'competitor-b': { pos: 'top-right', offsetX: 160, offsetY: 10 }
  }
}

const isMobile = () => window.innerWidth < 1024

class ComparisonManager {
  private viewports: Map<string, ViewportData> = new Map()
  private currentMesh: MeshVariant = 'orc'
  private currentMap: MapType = 'pbr'
  private initialized = false

  constructor() {
    // Lazy initialization - wait for section to be visible
    this.setupLazyInit()
  }

  private setupLazyInit(): void {
    const section = document.getElementById('comparison')
    if (!section) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.initialized) {
            this.init()
            this.initialized = true
            observer.disconnect()
          }
        })
      },
      { threshold: 0.1, rootMargin: '100px' }
    )
    observer.observe(section)
  }

  private init(): void {
    if (isMobile()) {
      // Mobile: carousel with static images
      this.setupMeshTabs()
      this.setupMobileCarousel()
      this.updateMeshDescription(this.currentMesh)
    } else {
      // Desktop: 3D viewports
      const viewportIds = ['competitor-a', 'competitor-b', 'bakehorse']

      viewportIds.forEach(id => {
        try {
          const viewport = new ThreeViewport(`#viewport-${id}`, {
            autoRotate: true,
            autoRotateSpeed: id === 'bakehorse' ? 0.975 : 0.65
          })

          this.viewports.set(id, { id, viewport })
        } catch (error) {
          console.error(`Failed to initialize viewport ${id}:`, error)
          this.viewports.set(id, { id, viewport: null })
        }
      })

      this.setupMeshTabs()
      this.setupGlobalMapToggles()
      this.setupViewportControls()
      this.updateMeshDescription(this.currentMesh)
      this.updateAnnotationOverlays(this.currentMesh)
      this.setAllMeshVariants(this.currentMesh)

      // Re-scale annotation offsets when window resizes
      window.addEventListener('resize', () => {
        this.updateAnnotationOverlays(this.currentMesh)
      })
    }
  }

  private setupMobileCarousel(): void {
    const carousel = document.querySelector('.mobile-comparison-carousel') as HTMLElement
    const dots = document.querySelectorAll('.carousel-dot')
    if (!carousel) return

    // Update dots on scroll
    carousel.addEventListener('scroll', () => {
      const slideWidth = carousel.firstElementChild?.clientWidth ?? 1
      const scrollLeft = carousel.scrollLeft
      const activeIndex = Math.round(scrollLeft / (slideWidth + 12)) // 12 = gap (0.75rem)

      dots.forEach((dot, i) => {
        dot.classList.toggle('active', i === activeIndex)
      })
    })

    // Dot click to scroll
    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        const slides = carousel.querySelectorAll('.mobile-carousel-slide')
        if (slides[i]) {
          slides[i].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
        }
      })
    })
  }

  private updateMobileCarouselImages(variant: MeshVariant): void {
    const folder = MESH_IMAGE_FOLDER[variant]
    const images = document.querySelectorAll('.mobile-carousel-img') as NodeListOf<HTMLImageElement>

    images.forEach(img => {
      const competitor = img.dataset.base
      if (competitor) {
        img.src = `/assets/CompetitorImages/${folder}/${competitor}.webp`
      }
    })
  }

  private setupMeshTabs(): void {
    const tabs = document.querySelectorAll('.mesh-tab')

    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const mesh = tab.getAttribute('data-mesh') as MeshVariant
        if (!mesh) return

        // Update active tab styling
        tabs.forEach(t => {
          t.classList.remove('chip-active')
        })
        tab.classList.add('chip-active')

        // Update description and all viewports
        this.currentMesh = mesh
        this.updateMeshDescription(mesh)

        this.updateAnnotationOverlays(mesh)
        if (isMobile()) {
          this.updateMobileCarouselImages(mesh)
        } else {
          this.setAllMeshVariants(mesh)
        }
      })
    })
  }

  private setupGlobalMapToggles(): void {
    const toggles = document.querySelectorAll('.global-map-toggle')

    toggles.forEach(toggle => {
      toggle.addEventListener('click', () => {
        const mapType = toggle.getAttribute('data-map') as MapType
        if (!mapType) return

        // Update active toggle styling
        toggles.forEach(t => {
          t.classList.remove('active', 'chip-active')
        })
        toggle.classList.add('active')

        // Update all viewports with the new map type
        this.currentMap = mapType
        this.setAllMapPreviews(mapType)
      })
    })
  }

  private setupViewportControls(): void {
    // Reset view buttons
    const resetButtons = document.querySelectorAll('.reset-view')
    resetButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const viewportId = btn.getAttribute('data-viewport')
        if (!viewportId) return

        const viewportData = this.viewports.get(viewportId)
        viewportData?.viewport?.resetView()
      })
    })

    // Fullscreen buttons
    const fullscreenButtons = document.querySelectorAll('.fullscreen-btn')
    fullscreenButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const viewportId = btn.getAttribute('data-viewport')
        if (!viewportId) return

        const viewportData = this.viewports.get(viewportId)
        viewportData?.viewport?.toggleFullscreen()
      })
    })
  }

  private updateMeshDescription(variant: MeshVariant): void {
    const info = MESH_INFO[variant]
    const promptEl = document.getElementById('mesh-prompt-text')
    const downloadEl = document.getElementById('mesh-download-btn') as HTMLAnchorElement
    const imageRefEl = document.getElementById('prompt-image-ref') as HTMLElement
    if (promptEl) promptEl.textContent = info.prompt
    if (downloadEl) downloadEl.href = info.downloadUrl
    if (imageRefEl) {
      if (info.imageRef) {
        imageRefEl.style.display = ''
        const thumb = imageRefEl.querySelector('.prompt-ref-thumb') as HTMLImageElement
        if (thumb) thumb.src = info.imageRef
      } else {
        imageRefEl.style.display = 'none'
      }
    }
  }

  private updateAnnotationOverlays(variant: MeshVariant): void {
    const folder = MESH_IMAGE_FOLDER[variant]
    const positions = ANNOTATION_POS[variant]
    const overlays = document.querySelectorAll('.viewport-annotation') as NodeListOf<HTMLImageElement>
    overlays.forEach(img => {
      const competitor = img.dataset.annotation
      if (!competitor || !positions[competitor]) return

      const info = positions[competitor]
      img.src = `/assets/Annotations/${folder}/${competitor}.png?v=2`
      img.dataset.pos = info.pos

      // Apply pixel offsets only on desktop viewport annotations
      const container = img.closest('.viewport-container') as HTMLElement | null
      if (container) {
        // Scale offsets proportionally to container width (reference: 400px at full desktop)
        const scale = Math.min(container.clientWidth / 400, 1)
        const parts: string[] = []
        if (info.pos === 'left-center' || info.pos === 'right-center') {
          parts.push('translateY(-50%)')
        }
        if (info.offsetX !== 0 || info.offsetY !== 0) {
          parts.push(`translate(${info.offsetX * scale}px, ${info.offsetY * scale}px)`)
        }
        img.style.transform = parts.join(' ') || ''
      }
    })
  }

  private async setAllMeshVariants(variant: MeshVariant): Promise<void> {
    // Show loading spinners on all viewports
    this.viewports.forEach(data => {
      data.viewport?.showLoading()
    })

    const promises: Promise<void>[] = []

    this.viewports.forEach(data => {
      if (data.viewport) {
        // Get the model path for this viewport and variant
        const modelPath = MODEL_PATHS[data.id]?.[variant]
        const promise = data.viewport.setMeshVariant(variant, modelPath).then(() => {
          // Apply the current map preview after model loads
          data.viewport?.setMapPreview(this.currentMap)
        })
        promises.push(promise)
      }
    })

    // Wait for ALL models to finish, then reveal all viewports together
    await Promise.all(promises)

    this.viewports.forEach(data => {
      data.viewport?.showContent()
    })
  }

  private setAllMapPreviews(mapType: MapType): void {
    this.viewports.forEach(data => {
      data.viewport?.setMapPreview(mapType)
    })
  }
}

export function initComparisonSection(): void {
  new ComparisonManager()
}
